import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Spinner,
  buttonClasses,
  cn,
  type PartnerOnboardingProgress,
  type PartnerOnboardingStep,
  type PartnerOrgProfileResponse,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { usePartnerMe } from "../auth/usePartnerMe";
import { useOrg } from "../shell/OrgContext";
import {
  usePharmacyOnboarding,
  type PharmacyOnboardingContextValue,
} from "../components/onboarding/PharmacyOnboardingContext";
import tortoiseGuide from "../assets/tortoise-guide.webp";

const STEPS: { id: PartnerOnboardingStep; label: string; title: string }[] = [
  { id: "welcome", label: "Welcome", title: "A good start for your pharmacy" },
  { id: "pharmacy_readiness", label: "Pharmacy setup", title: "Check your pharmacy setup" },
  {
    id: "working_with_patients",
    label: "Helping patients",
    title: "Help patients take the next step",
  },
  { id: "next_action", label: "Your next step", title: "Choose where to begin" },
];

const CONTROL =
  "min-h-11 max-w-full whitespace-normal px-[clamp(0.5rem,3vw,1rem)] text-base [overflow-wrap:anywhere]";

export function PharmacyOnboardingRoute(): ReactNode {
  const { orgId, res } = useOrg();
  const { error: accountError } = usePartnerMe();
  const onboarding = usePharmacyOnboarding();
  if (res.profile.organizationType !== "Pharmacy")
    return <Navigate to={`/orgs/${orgId}/home`} replace />;
  if (accountError) {
    return (
      <section className="mx-auto max-w-3xl py-6">
        <Alert tone="error" title="We couldn't load your account.">
          Reload the page to try again. Your introduction needs your account details to save your
          place.
        </Alert>
        <Link
          to={`/orgs/${orgId}/home`}
          className={buttonClasses("ghost", "md", `${CONTROL} mt-4`)}
        >
          Go to Home
        </Link>
      </section>
    );
  }
  if (!onboarding || onboarding.loading) {
    return (
      <section className="mx-auto max-w-3xl py-6">
        <p role="status" className="flex items-center gap-3 text-base">
          <Spinner /> Loading your introduction...
        </p>
        <Link
          to={`/orgs/${orgId}/home`}
          className={buttonClasses("ghost", "md", `${CONTROL} mt-4`)}
        >
          Go to Home
        </Link>
      </section>
    );
  }
  if (onboarding.loadError || !onboarding.progress) {
    return (
      <section className="mx-auto max-w-3xl py-6">
        <Alert tone="error" title="We couldn't load your introduction.">
          Try again or go to Home.
        </Alert>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button className={CONTROL} onClick={onboarding.reload}>
            Try again
          </Button>
          <Link to={`/orgs/${orgId}/home`} className={buttonClasses("ghost", "md", CONTROL)}>
            Go to Home
          </Link>
        </div>
      </section>
    );
  }
  return <Introduction onboarding={onboarding} initialProgress={onboarding.progress} />;
}

function Introduction({
  onboarding,
  initialProgress,
}: {
  onboarding: PharmacyOnboardingContextValue;
  initialProgress: PartnerOnboardingProgress;
}): ReactNode {
  const { orgId, res, isOrgAdmin } = useOrg();
  const navigate = useNavigate();
  const [review] = useState(initialProgress.status === "completed");
  const [stepIndex, setStepIndex] = useState(() =>
    initialProgress.status === "completed"
      ? 0
      : STEPS.findIndex((step) => step.id === initialProgress.step),
  );
  const titleRef = useRef<HTMLHeadingElement>(null);
  const mounted = useRef(false);
  const step = STEPS[stepIndex]!;
  const homePath = `/orgs/${orgId}/home`;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
    titleRef.current?.scrollIntoView?.({ block: "nearest", behavior: "instant" });
  }, [stepIndex]);

  async function move(next: number): Promise<void> {
    const nextStep = STEPS[next];
    if (!nextStep) return;
    const saved =
      review || (await onboarding.save({ version: 1, status: "in_progress", step: nextStep.id }));
    if (saved && mounted.current) setStepIndex(next);
  }

  async function leave(status: "deferred" | "completed", path = homePath): Promise<void> {
    const saved = review || (await onboarding.save({ version: 1, status, step: step.id }));
    if (saved && mounted.current) void navigate(path);
  }

  return (
    <section
      aria-label="Pharmacy introduction"
      className="mx-auto min-w-0 max-w-4xl text-base leading-relaxed [overflow-wrap:anywhere]"
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-slate-teal font-semibold">
          {review ? "Review your introduction" : "Getting started"}
        </p>
        <Link
          to={homePath}
          className="text-ember inline-flex min-h-11 items-center font-semibold underline underline-offset-2"
        >
          Go to Home
        </Link>
      </div>
      <p className="text-slate-teal mb-3" aria-live="polite">
        Step {stepIndex + 1} of {STEPS.length}: {step.label}
      </p>
      <ol
        aria-label="Introduction progress"
        className="mb-7 grid grid-cols-[repeat(auto-fit,minmax(min(100%,10rem),1fr))] gap-3"
      >
        {STEPS.map((item, index) => (
          <li
            key={item.id}
            aria-current={index === stepIndex ? "step" : undefined}
            className={cn(
              "min-w-0 border-t-4 pt-2",
              index <= stepIndex ? "border-ember text-midnight" : "border-haze text-slate-teal",
            )}
          >
            <span className="font-semibold">
              {index + 1}. {item.label}
            </span>
          </li>
        ))}
      </ol>
      <div className="border-haze min-w-0 rounded-2xl border bg-white p-[clamp(0.75rem,4vw,2rem)]">
        <h2
          ref={titleRef}
          tabIndex={-1}
          className="text-midnight font-serif text-2xl leading-snug sm:text-3xl"
        >
          {step.title}
        </h2>
        <div className="mt-5">
          {step.id === "welcome" && (
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1 space-y-4">
                <p>
                  DearBill helps you review support options with patients and refer them for help
                  with applications.
                </p>
                <p>
                  You are in <strong className="break-words">{res.name}</strong> as{" "}
                  {isOrgAdmin ? "an organization administrator" : "a team member"}.
                </p>
                <p>
                  We will check your pharmacy setup, cover how referrals work, and help you choose
                  your next task.
                </p>
                <p className="text-slate-teal">
                  We save your place as you continue, for your account and this pharmacy. You can
                  leave and return through Getting started.
                </p>
              </div>
              <img
                src={tortoiseGuide}
                alt=""
                width={144}
                height={144}
                className="hidden h-auto w-36 max-w-[22%] shrink-0 sm:block"
              />
            </div>
          )}
          {step.id === "pharmacy_readiness" && <PharmacyReadiness />}
          {step.id === "working_with_patients" && (
            <div className="space-y-5">
              <ol className="list-decimal space-y-4 pl-6">
                <li>
                  <strong>Ask before referring.</strong> Explain that DearBill can review support
                  options. Continue only with the patient&apos;s permission.
                </li>
                <li>
                  <strong>Choose how to help.</strong> From Refer a patient, share your
                  pharmacy&apos;s link or help complete the form together. Let the patient or their
                  authorized representative make application and enrollment choices.
                </li>
                <li>
                  <strong>Follow the review.</strong> Use Referrals for updates you can access.{" "}
                  {isOrgAdmin
                    ? "Organization administrators can see the pharmacy's referrals."
                    : "Team members can see their own referrals."}{" "}
                  Review, application submission and enrollment are separate steps.
                </li>
              </ol>
              <div className="bg-ice rounded-xl p-4">
                <p className="font-semibold">A referral is not an enrollment.</p>
                <p className="mt-2">
                  It starts a review. Eligibility and approval depend on the program. This
                  introduction does not collect a patient&apos;s permission or sign any agreement.
                </p>
              </div>
              <p>
                Use the patient form for requested information. Keep patient details out of general
                support messages, and sign out when leaving a shared workstation.
              </p>
              <p className="text-slate-teal">
                The Medicare Prescription Payment Plan (M3P) spreads out-of-pocket costs for covered
                Part D prescriptions over the remaining months of the calendar year. It does not
                lower yearly drug costs or change coverage. Calculator results are estimates, not
                quotes.
              </p>
            </div>
          )}
          {step.id === "next_action" && (
            <div className="space-y-5">
              <p>
                {res.status === "active"
                  ? "Your pharmacy is active. From Home, select Refer a patient when you are ready to help someone."
                  : "Your pharmacy is not active yet. You can explore payment estimates while setup or DearBill review is in progress."}
              </p>
              <p>
                Finishing this introduction does not activate your pharmacy, submit an application,
                or create a referral.
              </p>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,18rem),1fr))] gap-3">
                <Button
                  variant="outlined"
                  className={CONTROL}
                  disabled={onboarding.saving}
                  onClick={() => void leave("completed", `/orgs/${orgId}/cost-tool`)}
                >
                  Finish and explore estimates
                </Button>
                <Button
                  variant="soft"
                  className={CONTROL}
                  disabled={onboarding.saving}
                  onClick={() =>
                    void leave(
                      "completed",
                      `/orgs/${orgId}/${res.status === "active" ? "forms" : "organization"}`,
                    )
                  }
                >
                  {res.status === "active"
                    ? "Finish and view referrals"
                    : "Finish and view pharmacy setup"}
                </Button>
              </div>
              <p className="text-slate-teal">
                For a quick look at the portal controls, choose Pharmacy tour in the top bar. For
                support, choose Need help? Call at the top of the page.
              </p>
            </div>
          )}
        </div>
        {onboarding.saveError && (
          <Alert tone="error" title="Your progress wasn't saved." className="mt-6">
            Try your last action again. You can also{" "}
            <Link to={homePath} className="font-semibold underline">
              leave without saving
            </Link>{" "}
            and return later.
          </Alert>
        )}
        <div className="border-haze mt-7 flex flex-wrap items-center justify-between gap-3 border-t pt-5">
          <div className="flex min-w-0 max-w-full flex-[1_1_16rem] flex-wrap gap-2">
            {stepIndex > 0 && (
              <Button
                variant="soft"
                className={CONTROL}
                disabled={onboarding.saving}
                onClick={() => void move(stepIndex - 1)}
              >
                Back
              </Button>
            )}
            <Button
              variant="ghost"
              className={CONTROL}
              disabled={onboarding.saving}
              onClick={() => void leave("deferred")}
            >
              {review ? "Close introduction" : "Skip for now"}
            </Button>
          </div>
          <Button
            className={CONTROL}
            disabled={onboarding.saving}
            onClick={() =>
              void (stepIndex === STEPS.length - 1 ? leave("completed") : move(stepIndex + 1))
            }
          >
            {onboarding.saving
              ? "Saving..."
              : stepIndex === STEPS.length - 1
                ? review
                  ? "Return to Home"
                  : "Finish introduction"
                : "Continue"}
          </Button>
        </div>
      </div>
    </section>
  );
}

function PharmacyReadiness(): ReactNode {
  const { orgId, res, setRes, isOrgAdmin } = useOrg();
  const api = useApi();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(false);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);

  async function refresh(): Promise<void> {
    if (checking) return;
    const controller = new AbortController();
    request.current = controller;
    setChecking(true);
    setError(false);
    try {
      const next = await api<PartnerOrgProfileResponse>(`/api/partner/orgs/${orgId}/profile`, {
        signal: controller.signal,
      });
      if (!controller.signal.aborted && next.orgId === orgId) setRes(next);
    } catch {
      if (!controller.signal.aborted) setError(true);
    } finally {
      if (!controller.signal.aborted) setChecking(false);
    }
  }

  const profileNeeded = res.blockers.includes("profile_incomplete");
  const termsNeeded = res.agreementRequired && res.blockers.includes("document_unsigned");
  const needsAction = profileNeeded || termsNeeded;
  return (
    <div className="space-y-5">
      <div className="bg-ice rounded-xl p-4">
        <p className="text-midnight font-semibold">
          {res.status === "active"
            ? "Your pharmacy is active"
            : res.underReview
              ? "Your pharmacy is under review"
              : "Your pharmacy is not active yet"}
        </p>
        <p className="mt-2">
          {res.status === "active"
            ? "You can refer patients with their permission."
            : res.underReview
              ? "Your pharmacy's setup items are complete. DearBill still needs to review activation. Referrals are available after activation."
              : "Referrals become available after setup and DearBill activation. Review the items below to see what is still needed."}
        </p>
      </div>
      <dl className="divide-haze divide-y">
        <ReadinessRow
          title="Organization profile"
          detail={
            profileNeeded
              ? "Details are still needed"
              : res.complete
                ? "Complete"
                : "Check Organization for details"
          }
        />
        {res.agreementRequired && (
          <ReadinessRow
            title="DearBill Terms & Conditions"
            detail={
              termsNeeded ? "An organization administrator needs to accept" : "Acceptance recorded"
            }
          />
        )}
        <ReadinessRow
          title="Pharmacy activation"
          detail={
            res.status === "active"
              ? "Active"
              : needsAction
                ? "Setup needed before activation"
                : res.underReview
                  ? "Pending DearBill review"
                  : "Not active"
          }
        />
      </dl>
      {res.profileLocked && needsAction ? (
        <p>
          The profile is locked. Use Need help? Call at the top of the page to ask DearBill about
          the remaining setup items.
        </p>
      ) : needsAction ? (
        <p>
          {isOrgAdmin
            ? "Open Organization to complete the profile and any required terms. Draft changes save there, but you still need to submit for review. Return here through Resume introduction."
            : "Your organization administrator completes the profile and accepts the terms. Ask them to review the remaining items. You can view the pharmacy details below."}
        </p>
      ) : (
        <p>
          {isOrgAdmin
            ? "You can review the details on file in Organization. Team access is managed in the Team tab."
            : "Your organization administrator manages profile changes and team access. You can view the details on file in Organization."}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <Link
          to={`/orgs/${orgId}/organization`}
          className={buttonClasses("secondary", "md", CONTROL)}
        >
          {needsAction && isOrgAdmin && !res.profileLocked
            ? "Complete pharmacy setup"
            : "View pharmacy details"}
        </Link>
        <Button
          variant="soft"
          className={CONTROL}
          onClick={() => void refresh()}
          disabled={checking}
        >
          {checking ? "Checking..." : "Check latest status"}
        </Button>
        {isOrgAdmin && (
          <Link to={`/orgs/${orgId}/members`} className={buttonClasses("ghost", "md", CONTROL)}>
            Manage team
          </Link>
        )}
      </div>
      {error && (
        <Alert tone="error" title="We couldn't refresh the pharmacy status.">
          The details above are the last ones loaded. Try again or contact DearBill.
        </Alert>
      )}
      <p className="text-slate-teal">
        You can continue this introduction while setup or review is pending.
      </p>
    </div>
  );
}

function ReadinessRow({ title, detail }: { title: string; detail: string }): ReactNode {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] gap-1 py-3 sm:gap-4">
      <dt className="font-semibold">{title}</dt>
      <dd className="text-slate-teal">{detail}</dd>
    </div>
  );
}
